/**
 * Collection of special characters that are considered punctuation in the context of mentions.
 * These characters are escaped for use in regex patterns.
 * Example: "r. " in "Mr. Smith"
 */
export const PUNCTUATION = '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;';

/**
 * Pattern to match a name that starts with a capital letter and is not followed by whitespace or punctuation.
 * Used to identify potential mention names in text.
 */
export const NAME = `\\b[A-Z][^\\s${PUNCTUATION}]`;

/**
 * Character that triggers the mention functionality. Currently only '@' is supported.
 */
export const TRIGGERS = '@';

/**
 * Pattern to match valid characters in a mention.
 * Excludes trigger characters, punctuation, and whitespace.
 */
export const VALID_CHARS = `[^${TRIGGERS}${PUNCTUATION}\\s]`;

/**
 * Pattern to match valid joining characters between words in a mention.
 * Supports:
 * - Period followed by space or end of string (e.g., "Mr. Smith")
 * - Space between words (e.g., "Josh Duck")
 * - Punctuation marks (e.g., hyphen in "Salier-Hellendag")
 */
export const VALID_JOINS
    = `${'(?:'
    + '\\.[ |$]|'
    + ' |'
    + '['}${PUNCTUATION
    }]|`
    + ')';

export const LENGTH_LIMIT = 75;

/**
 * Main regex pattern for matching @mentions in text.
 * Matches:
 * 1. Start of string, whitespace, or opening parenthesis
 * 2. @ symbol
 * 3. Valid characters and joins up to LENGTH_LIMIT
 * 4. End of string
 *
 * Example matches:
 * - "@John"
 * - "Hello @John"
 * - "(@John)"
 */
export const AtSignMentionsRegex = new RegExp(
    `${'(^|\\s|\\()('
    + '['}${TRIGGERS
    }]`
    + `((?:${VALID_CHARS
    }${VALID_JOINS
    }){0,${LENGTH_LIMIT
    }})`
    + ')$',
);

export const ALIAS_LENGTH_LIMIT = 50;

/**
 * Alternative regex pattern for matching @mentions that only allows valid characters
 * without joins. Used for simpler alias matching.
 *
 * Example matches:
 * - "@JohnDoe"
 * - "Hello @JohnDoe"
 * - "(@JohnDoe)"
 */
export const AtSignMentionsRegexAliasRegex = new RegExp(
    `${'(^|\\s|\\()('
    + '['}${TRIGGERS
    }]`
    + `((?:${VALID_CHARS
    }){0,${ALIAS_LENGTH_LIMIT
    }})`
    + ')$',
);

export const SUGGESTION_LIST_LENGTH_LIMIT = 5;
